import omit from 'lodash.omit';

import { getDepth } from '@spazious/dollhouse-selector';

import { FCompositionTypesEnum } from '../types/index.js';
import { encodeFCompositionId } from '../utils/encodeFCompositionId.js';

import { MigrationStateV1_0_0 } from './migration-v1.0.0.js';
import { Migration, MigrationVersionEnum } from './types.js';

enum FShallowNodeTypesEnum {
  Furniture = 1,
  Decoration = 2,
  FComposition = 3,
  FPolygon = 4,
  FGroup = 5,
  FArea = 6,
  Measurement = 7,
  Annotation = 8,
  FOverride = 255,
}

export type MigrationStateV2_0_0 = (
  | {
      type: number;
      selector: string;
      id: number;
      attrs?: Record<string, any>;
      meta?: Record<string, any>;
    }
  | {
      type: FShallowNodeTypesEnum.FOverride;
      selector: string;
    }
)[];

const oldDecodeFCompositionId = (id: number) => ({
  type: id >> 8,
  subType: id & ((1 << (8 - 1)) - 1),
});

const fcompositionMapping = {
  farrangement: FCompositionTypesEnum.FArrangement,
  fcomposition: FCompositionTypesEnum.FEnsemble,
} as const;

export const migration_2_0_0: Migration<
  MigrationVersionEnum.v2_0_0,
  MigrationStateV1_0_0,
  MigrationStateV2_0_0
> = {
  version: MigrationVersionEnum.v2_0_0,
  check: (state) =>
    Array.isArray(state) &&
    state.length !== 0 &&
    state.some((node) => typeof node.type === 'string'),
  migrate: (state) => {
    const shallowTypeMapping = {
      furniture: FShallowNodeTypesEnum.Furniture,
      decoration: FShallowNodeTypesEnum.Decoration,
      fcomposition: FShallowNodeTypesEnum.FComposition,
      fgroup: FShallowNodeTypesEnum.FGroup,
      fpolygon: FShallowNodeTypesEnum.FPolygon,
      farea: FShallowNodeTypesEnum.FArea,
      measurement: FShallowNodeTypesEnum.Measurement,
    };
    return state.map((node) => {
      const { type, ...restProps } = node;

      if (typeof type === 'string') {
        if (Object.keys(fcompositionMapping).includes(type)) {
          const { subType, type } = oldDecodeFCompositionId(node.id);
          return {
            type: FShallowNodeTypesEnum.FComposition,
            ...restProps,
            id: encodeFCompositionId(type, subType),
          };
        }

        if (!(shallowTypeMapping as any)[type]) {
          throw new Error(`Unknown type: ${type}`);
        }

        // Override the type if the depth is greater than 1
        if (getDepth(node.selector) > 1) {
          return {
            type: FShallowNodeTypesEnum.FOverride,
            ...omit(restProps, 'id'),
          };
        }

        return {
          type: (shallowTypeMapping as any)[type],
          ...restProps,
        };
      }

      return node;
    }) as MigrationStateV2_0_0;
  },
};
